import { SmsSettingsCard } from "@/components/settings/SmsSettingsCard";
import { useEffect, useState } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sliders, Loader2, Save, Image as ImageIcon, X, CalendarRange, ShieldAlert, Mail } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import SubcontractorDocTypesCard from "@/components/subcontractor/SubcontractorDocTypesCard";

/**
 * AppConfigurationPage — Réglages applicatifs (org-scoped).
 *
 * Toggles métier qui modifient le comportement de l'application :
 * - Inclure les commandes "En pause" dans les calculs d'encours / argent dehors.
 */
const AppConfigurationPage = () => {
  const { isOrgAdmin, isSuperAdmin } = useOrg();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const canEdit = isOrgAdmin || isSuperAdmin;

  useEffect(() => {
    let cancelled = false;
    api.getAppConfig()
      .then((res) => { if (!cancelled) setConfig(res.data || {}); })
      .catch(() => { if (!cancelled) setConfig({}); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleToggle = (key, value) => {
    setConfig((c) => ({ ...c, [key]: value }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Sanity-check : si l'utilisateur a effacé un champ numérique requis, on
      // revient au défaut (1) avant l'envoi pour éviter une 422 backend.
      const safe = {
        ...config,
        fiscal_year_start_day: (config?.fiscal_year_start_day === "" || config?.fiscal_year_start_day == null)
          ? 1 : config.fiscal_year_start_day,
        fiscal_year_start_month: (config?.fiscal_year_start_month === "" || config?.fiscal_year_start_month == null)
          ? 1 : config.fiscal_year_start_month,
      };
      const res = await api.updateAppConfig(safe);
      setConfig(res.data || {});
      setDirty(false);
      toast.success("Configuration enregistrée");
    } catch (e) {
      toast.error(getErrorMessage(e, "Erreur de sauvegarde"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="app-config-page">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Sliders className="h-5 w-5 text-primary" />
            Configuration de l'application
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Ces options sont appliquées à l'ensemble de l'organisation. Seul un gestionnaire d'organisation peut les modifier.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div
            className="flex items-start justify-between gap-4 p-4 border rounded-md bg-card"
            data-testid="config-include-paused-row"
          >
            <div className="flex-1">
              <Label htmlFor="include-paused" className="text-sm font-medium">
                Inclure les commandes « En pause » dans l'encours et l'argent dehors
              </Label>
              <p className="text-xs text-muted-foreground mt-1.5">
                Par défaut désactivé. Si activé, les commandes mises manuellement en pause contribuent
                aux calculs financiers (encours client, argent dehors).
              </p>
            </div>
            <Switch
              id="include-paused"
              checked={!!config?.include_paused_in_outstanding}
              onCheckedChange={(v) => handleToggle("include_paused_in_outstanding", v)}
              disabled={!canEdit}
              data-testid="toggle-include-paused"
            />
          </div>

          <div
            className="flex items-start justify-between gap-4 p-4 border rounded-md bg-card"
            data-testid="config-include-paused-stock-row"
          >
            <div className="flex-1">
              <Label htmlFor="include-paused-stock" className="text-sm font-medium">
                Inclure les commandes « En pause » dans le Stock de commandes
              </Label>
              <p className="text-xs text-muted-foreground mt-1.5">
                Par défaut désactivé. Si activé, les commandes en pause sont comptabilisées
                dans la valeur du stock (dashboard + prévision de fin de stock).
              </p>
            </div>
            <Switch
              id="include-paused-stock"
              checked={!!config?.include_paused_in_stock}
              onCheckedChange={(v) => handleToggle("include_paused_in_stock", v)}
              disabled={!canEdit}
              data-testid="toggle-include-paused-stock"
            />
          </div>

          <div
            className="flex items-start justify-between gap-4 p-4 border rounded-md bg-card"
            data-testid="config-include-paused-revenue-row"
          >
            <div className="flex-1">
              <Label htmlFor="include-paused-revenue" className="text-sm font-medium">
                Inclure les commandes « En pause » dans le Suivi CA
              </Label>
              <p className="text-xs text-muted-foreground mt-1.5">
                Par défaut désactivé. Si activé, les commandes en pause apparaissent
                dans la page Suivi CA pour saisir leur avancement mensuel.
              </p>
            </div>
            <Switch
              id="include-paused-revenue"
              checked={!!config?.include_paused_in_revenue_tracking}
              onCheckedChange={(v) => handleToggle("include_paused_in_revenue_tracking", v)}
              disabled={!canEdit}
              data-testid="toggle-include-paused-revenue"
            />
          </div>

          <div
            className="flex items-start justify-between gap-4 p-4 border rounded-md bg-card"
            data-testid="config-include-subcontracting-row"
          >
            <div className="flex-1">
              <Label htmlFor="include-subcontracting" className="text-sm font-medium">
                Inclure la sous-traitance dans le calcul des jours de stock
              </Label>
              <p className="text-xs text-muted-foreground mt-1.5">
                Par défaut désactivé. Si désactivé, les commandes assignées à un sous-traitant restent comptées
                dans le <strong>montant</strong> du stock mais sont <strong>exclues du calcul du nombre de jours restants</strong>
                (les jours représentent uniquement le travail à réaliser par les équipes internes).
              </p>
            </div>
            <Switch
              id="include-subcontracting"
              checked={!!config?.include_subcontracting_in_stock_days}
              onCheckedChange={(v) => handleToggle("include_subcontracting_in_stock_days", v)}
              disabled={!canEdit}
              data-testid="toggle-include-subcontracting"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !canEdit}
              data-testid="save-app-config-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
          {!canEdit && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              Cette page est en lecture seule pour votre rôle. Contactez l'administrateur de l'organisation pour modifier ces options.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Fiscal year card */}
      <Card data-testid="fiscal-year-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <CalendarRange className="h-5 w-5 text-primary" />
            Exercice comptable (année fiscale)
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Définit la date de début de l'exercice comptable de l'organisation. Les graphiques utilisant
            « Année comptable en cours » s'ajustent automatiquement selon cette période. Par défaut : 1er janvier (année civile).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 max-w-sm">
            <div>
              <Label htmlFor="fiscal-day" className="text-sm">Jour de début</Label>
              <Input
                id="fiscal-day"
                type="number"
                min={1}
                max={31}
                value={config?.fiscal_year_start_day ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { handleToggle("fiscal_year_start_day", ""); return; }
                  const n = parseInt(raw, 10);
                  if (!Number.isNaN(n)) handleToggle("fiscal_year_start_day", Math.min(31, Math.max(1, n)));
                }}
                onBlur={() => {
                  const v = config?.fiscal_year_start_day;
                  if (v === "" || v === null || v === undefined || Number.isNaN(parseInt(v, 10))) {
                    handleToggle("fiscal_year_start_day", 1);
                  }
                }}
                disabled={!canEdit}
                className="mt-1.5"
                data-testid="fiscal-day-input"
              />
            </div>
            <div>
              <Label htmlFor="fiscal-month" className="text-sm">Mois de début</Label>
              <Input
                id="fiscal-month"
                type="number"
                min={1}
                max={12}
                value={config?.fiscal_year_start_month ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { handleToggle("fiscal_year_start_month", ""); return; }
                  const n = parseInt(raw, 10);
                  if (!Number.isNaN(n)) handleToggle("fiscal_year_start_month", Math.min(12, Math.max(1, n)));
                }}
                onBlur={() => {
                  const v = config?.fiscal_year_start_month;
                  if (v === "" || v === null || v === undefined || Number.isNaN(parseInt(v, 10))) {
                    handleToggle("fiscal_year_start_month", 1);
                  }
                }}
                disabled={!canEdit}
                className="mt-1.5"
                data-testid="fiscal-month-input"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Exemple : <strong>01 / 09</strong> = exercice du 1<sup>er</sup> septembre au 31 août.
          </p>
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !canEdit}
              data-testid="save-fiscal-year-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Forecast variance threshold card (Phase 178) */}
      <Card data-testid="forecast-variance-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Sliders className="h-5 w-5 text-primary" />
            Seuil d&apos;alerte sur les écarts prévu / réel (matières)
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Seuil (%) au-dessus duquel un écart entre la consommation prévue et la consommation réelle déclenche une alerte dans la vue Analytics « Écarts Prévu / Réel ». Valeur par défaut : 10 %.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 max-w-sm items-end">
            <div>
              <Label htmlFor="variance-threshold" className="text-sm">Seuil (%)</Label>
              <Input
                id="variance-threshold"
                type="number"
                min={0}
                max={100}
                step={1}
                value={config?.forecast_actual_variance_threshold_pct ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") { handleToggle("forecast_actual_variance_threshold_pct", ""); return; }
                  const n = parseFloat(raw);
                  if (!Number.isNaN(n)) handleToggle("forecast_actual_variance_threshold_pct", Math.max(0, Math.min(100, n)));
                }}
                onBlur={() => {
                  const v = config?.forecast_actual_variance_threshold_pct;
                  if (v === "" || v === null || v === undefined || Number.isNaN(parseFloat(v))) {
                    handleToggle("forecast_actual_variance_threshold_pct", 10);
                  }
                }}
                disabled={!canEdit}
                className="mt-1.5"
                data-testid="variance-threshold-input"
              />
            </div>
          </div>
          <div className="flex justify-end pt-1">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !canEdit}
              data-testid="save-variance-threshold-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resend sender email card (Phase 182) */}
      <SmsSettingsCard config={config} onToggle={handleToggle} canEdit={canEdit} onSave={handleSave} saving={saving} dirty={dirty} />

      <Card data-testid="sender-email-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Adresse expéditeur des emails (Resend)
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Adresse utilisée comme expéditeur de tous les emails envoyés depuis Bati Assistant
            (devis, factures, relances, invitations, notifications…). Le domaine doit obligatoirement
            être <strong>vérifié</strong> dans <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-primary underline">resend.com/domains</a> sinon Resend refusera l&apos;envoi.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-w-lg space-y-2">
            <Label htmlFor="sender-email" className="text-sm">
              Adresse expéditeur (ex : <code className="bg-zinc-100 px-1 rounded text-[11px]">Sitex Isolation &lt;noreply@sitex-isolation.com&gt;</code>)
            </Label>
            <Input
              id="sender-email"
              type="text"
              placeholder="noreply@votre-domaine.fr"
              value={config?.sender_email ?? ""}
              onChange={(e) => handleToggle("sender_email", e.target.value)}
              disabled={!canEdit}
              data-testid="sender-email-input"
            />
            <p className="text-[11px] text-muted-foreground">
              Tu peux utiliser un format simple (<code>noreply@domaine.fr</code>) ou avec nom d&apos;affichage (<code>Mon Entreprise &lt;noreply@domaine.fr&gt;</code>).
              Laisse vide pour revenir au comportement par défaut (testing Resend).
            </p>
          </div>
          <div className="flex justify-end pt-1">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !canEdit}
              data-testid="save-sender-email-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>




      {/* Subcontractor compliance card (Phase 175) */}
      <Card data-testid="subcontractor-compliance-config-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            Conformité documentaire des sous-traitants
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Configure les seuils d'alerte et le modèle d'email envoyé automatiquement aux sous-traitants quand un document approche de l'expiration.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="dashboard-alert-days" className="text-sm font-medium">
                Alerte tableau de bord (X jours avant expiration)
              </Label>
              <Input
                id="dashboard-alert-days"
                type="number" min={1} max={365}
                value={config?.subcontractor_dashboard_alert_days ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") return handleToggle("subcontractor_dashboard_alert_days", "");
                  const n = parseInt(raw, 10);
                  if (!Number.isNaN(n)) handleToggle("subcontractor_dashboard_alert_days", Math.min(365, Math.max(1, n)));
                }}
                onBlur={() => {
                  const v = config?.subcontractor_dashboard_alert_days;
                  if (v === "" || v == null || Number.isNaN(parseInt(v, 10))) handleToggle("subcontractor_dashboard_alert_days", 30);
                }}
                disabled={!canEdit}
                className="mt-1.5"
                data-testid="dashboard-alert-days-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Affiche l'alerte sur le dashboard pour les documents expirant dans ce délai.
              </p>
            </div>
            <div>
              <Label htmlFor="email-alert-days" className="text-sm font-medium flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                Email automatique (Y jours avant expiration)
              </Label>
              <Input
                id="email-alert-days"
                type="number" min={1} max={365}
                value={config?.subcontractor_email_alert_days ?? ""}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === "") return handleToggle("subcontractor_email_alert_days", "");
                  const n = parseInt(raw, 10);
                  if (!Number.isNaN(n)) handleToggle("subcontractor_email_alert_days", Math.min(365, Math.max(1, n)));
                }}
                onBlur={() => {
                  const v = config?.subcontractor_email_alert_days;
                  if (v === "" || v == null || Number.isNaN(parseInt(v, 10))) handleToggle("subcontractor_email_alert_days", 15);
                }}
                disabled={!canEdit}
                className="mt-1.5"
                data-testid="email-alert-days-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Envoie automatiquement l'email au sous-traitant si son switch "Email automatique" est activé.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="email-subject" className="text-sm font-medium">Objet de l'email</Label>
            <Input
              id="email-subject"
              value={config?.subcontractor_email_subject ?? ""}
              onChange={(e) => handleToggle("subcontractor_email_subject", e.target.value)}
              disabled={!canEdit}
              className="mt-1.5"
              data-testid="email-subject-input"
            />
          </div>

          <div>
            <Label htmlFor="email-body" className="text-sm font-medium">Corps de l'email</Label>
            <Textarea
              id="email-body"
              value={config?.subcontractor_email_body ?? ""}
              onChange={(e) => handleToggle("subcontractor_email_body", e.target.value)}
              disabled={!canEdit}
              className="mt-1.5 font-mono text-xs min-h-[180px]"
              data-testid="email-body-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Variables disponibles : <code className="bg-zinc-100 px-1 rounded">{"{nom_sous_traitant}"}</code> et <code className="bg-zinc-100 px-1 rounded">{"{liste_documents_manquants_ou_expires}"}</code>
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <Button onClick={handleSave} disabled={!dirty || saving || !canEdit} data-testid="save-compliance-config-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Doc types library (Phase 175) */}
      <SubcontractorDocTypesCard canEdit={canEdit} />

      {/* Branding card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-primary" />
            Identité de l'organisation
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Le nom et le logo seront utilisés dans les PDF générés (fiches de chantier SAV, devis, etc.).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="org-name" className="text-sm">Nom de l'organisation</Label>
            <Input
              id="org-name"
              value={config?.organization_name || ""}
              onChange={(e) => handleToggle("organization_name", e.target.value)}
              disabled={!canEdit}
              placeholder="Ex: SITEX Isolation"
              className="mt-1.5"
              data-testid="org-name-input"
            />
          </div>
          <div>
            <Label className="text-sm">Logo (PNG/JPG, max 1 Mo)</Label>
            <div className="flex items-start gap-3 mt-1.5">
              {config?.organization_logo_base64 ? (
                <div className="relative border rounded-md p-2 bg-white">
                  <img src={config.organization_logo_base64} alt="Logo" className="h-20 w-20 object-contain" />
                  <button
                    onClick={() => handleToggle("organization_logo_base64", null)}
                    disabled={!canEdit}
                    className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5 hover:bg-destructive hover:text-white"
                    data-testid="remove-logo-btn"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ) : (
                <div className="border-dashed border-2 rounded-md h-20 w-20 flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1">
                <label className="inline-block">
                  <Button variant="outline" disabled={!canEdit} asChild>
                    <span data-testid="upload-logo-btn" className="cursor-pointer">
                      {config?.organization_logo_base64 ? "Remplacer" : "Téléverser"}
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={!canEdit}
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 1024 * 1024) {
                        toast.error("Logo trop volumineux (max 1 Mo)");
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => handleToggle("organization_logo_base64", reader.result);
                      reader.readAsDataURL(f);
                    }}
                  />
                </label>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Format conseillé : carré ou rectangulaire horizontal, fond transparent.
                </p>
              </div>
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={!dirty || saving || !canEdit}
              data-testid="save-branding-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppConfigurationPage;
